import type { AuthStore, AuthUser, UserRole } from '../types/auth';
import { userDisplayNameOr } from '../utils/userDisplayName';

/**
 * Fase 16B-2: espejo en frontend de la matriz de permisos backend
 * (`packages/backend/src/domain/permissions/permissions.ts`, ver `docs/PERMISSIONS_MODEL.md`).
 *
 * IMPORTANTE: esto es solo para experiencia (ocultar/deshabilitar controles, navegación por rol).
 * La seguridad real siempre se valida en el backend — si alguien llama a la API directamente sin
 * pasar por esta capa, el backend igual rechaza la acción con su propio código de error. Nunca
 * agregues aquí una regla que el backend no aplique; si el backend cambia, esto debe cambiar junto.
 */

export type { UserRole };

const ROLE_PERMISSIONS: Record<UserRole, ReadonlySet<string>> = {
  OWNER: new Set([
    'users.manage', 'stores.manage', 'financials.view', 'financials.viewCosts', 'loyverse.view', 'loyverse.manage',
    'inventory.view', 'inventory.manageStructure', 'inventory.requestSupplier', 'inventory.requestIngredient', 'inventory.approveCatalog',
    'purchases.manage', 'purchases.confirm', 'purchases.void', 'stock.adjust', 'stock.transfer',
    'inventory.recordWaste', 'inventory.reviewWaste', 'inventory.countAssigned',
    'salesInventory.preview', 'salesInventory.post', 'salesInventory.reverse', 'salesInventory.reprocess',
    'shiftClose.create', 'shiftClose.view', 'shiftClose.void', 'dataAudit.view',
    'otherExpenses.view', 'otherExpenses.create', 'otherExpenses.confirm', 'otherExpenses.void',
    'otherSales.view', 'otherSales.create', 'otherSales.update', 'otherSales.confirm', 'otherSales.void',
    'voidRequests.view', 'voidRequests.review',
    'forms.view', 'forms.manage', 'forms.submit', 'forms.responses.view', 'forms.responses.review',
  ]),
  MANAGER: new Set([
    'users.manage', 'stores.view', 'financials.view', 'financials.viewCosts', 'loyverse.view',
    'inventory.view', 'inventory.manageStructure', 'inventory.requestSupplier', 'inventory.requestIngredient', 'inventory.approveCatalog',
    'purchases.manage', 'purchases.confirm', 'purchases.void', 'stock.adjust', 'stock.transfer',
    'inventory.recordWaste', 'inventory.reviewWaste', 'inventory.countAssigned',
    'salesInventory.preview', 'salesInventory.post', 'salesInventory.reverse', 'salesInventory.reprocess',
    'shiftClose.create', 'shiftClose.view', 'shiftClose.void',
    'otherExpenses.view', 'otherExpenses.create', 'otherExpenses.confirm', 'otherExpenses.void',
    'otherSales.view', 'otherSales.create', 'otherSales.update', 'otherSales.confirm', 'otherSales.void',
    'voidRequests.view', 'voidRequests.review',
    'forms.view', 'forms.manage', 'forms.submit', 'forms.responses.view', 'forms.responses.review',
  ]),
  // Fase 21A: un EMPLOYEE ahora opera módulos básicos de SU tienda asignada (compras, otros
  // gastos, ventas otros, cortes de turno) — crear/ver/confirmar, nunca anular directamente
  // (sin otherExpenses.void/otherSales.void/purchases.void/shiftClose.void). En su lugar puede
  // crear una solicitud de anulación (voidRequests.create) para que un MANAGER/OWNER la apruebe
  // o rechace (voidRequests.review). Espejo exacto de EMPLOYEE_PERMISSIONS en el backend.
  EMPLOYEE: new Set([
    'shiftClose.create', 'shiftClose.view',
    'otherExpenses.view', 'otherExpenses.create', 'otherExpenses.confirm',
    'otherSales.view', 'otherSales.create', 'otherSales.confirm',
    // Fase 21A (hallazgo en vivo): registrar una compra requiere poder LEER insumos/unidades/
    // proveedores (elegirlos en el formulario) — sin esto, la pantalla de Compras nunca cargaba
    // para EMPLOYEE. Sigue sin 'inventory.manageStructure': no puede crear/editar ese catálogo.
    'inventory.view',
    // Captura compras, pero NO las confirma: la suya siempre la aprueba un dueño o un gerente
    // (espejo de EMPLOYEE_PERMISSIONS sin inventory.confirmPurchases en el backend).
    'purchases.manage',
    // Fase 26A: un EMPLOYEE puede PROPONER un proveedor o insumo nuevo (quedan PENDING_APPROVAL) para
    // no bloquearse al capturar una compra física; sigue sin inventory.manageStructure (no crea/edita
    // catálogo activo) ni inventory.approveCatalog (no aprueba). El backend enruta la propuesta.
    'inventory.requestSupplier', 'inventory.requestIngredient',
    // F1 (inventory-waste-employee): registra mermas de SUS tiendas desde la pantalla «Mermas» (fuera
    // de Inventario). Sigue sin 'stock.adjust', 'stock.transfer' ni 'inventory.reviewWaste'.
    'inventory.recordWaste',
    // F4 (inventory-count-sheets): cuenta a ciegas SOLO la lista que le asignaron. Sigue sin 'stock.adjust'.
    'inventory.countAssigned',
    'voidRequests.create', 'voidRequests.view',
    // Fase 23B: un EMPLOYEE responde formularios de su tienda (no administra ni ve respuestas ajenas).
    'forms.submit',
  ]),
};

function hasPermission(role: UserRole | undefined, permission: string): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].has(permission);
}

// --- Helpers pedidos explícitamente por la Fase 16B-2, en el mismo orden que el brief ---

export function canViewFinancials(role: UserRole | undefined): boolean {
  return hasPermission(role, 'financials.view');
}

// Fase 22C-3: costo/margen de Ventas Otros solo para roles con financials.viewCosts (OWNER/MANAGER).
// La seguridad real la aplica el backend; esto solo oculta la sección en la UI.
export function canViewCosts(role: UserRole | undefined): boolean {
  return hasPermission(role, 'financials.viewCosts');
}

export function canManageUsers(role: UserRole | undefined): boolean {
  return hasPermission(role, 'users.manage');
}

export function canManageStores(role: UserRole | undefined): boolean {
  return hasPermission(role, 'stores.manage');
}

export function canManageLoyverse(role: UserRole | undefined): boolean {
  return hasPermission(role, 'loyverse.manage');
}

export function canViewOwnerDashboard(role: UserRole | undefined): boolean {
  // El backend permite OWNER/MANAGER intentarlo; el permiso financiero real depende además de la
  // tienda elegida (ver `getFinancialStores`). EMPLOYEE nunca.
  return role === 'OWNER' || role === 'MANAGER';
}

export function canManageInventoryStructure(role: UserRole | undefined): boolean {
  return hasPermission(role, 'inventory.manageStructure');
}

// Fase 26A: proponer un proveedor/insumo nuevo (queda pendiente de aprobación). OWNER/MANAGER también
// lo tienen (crean directo en ACTIVE); un EMPLOYEE solo propone. La seguridad real la aplica el
// backend (supplierService/inventoryService deciden ACTIVE vs PENDING_APPROVAL según el rol).
export function canProposeSupplier(role: UserRole | undefined): boolean {
  return hasPermission(role, 'inventory.requestSupplier');
}
export function canProposeIngredient(role: UserRole | undefined): boolean {
  return hasPermission(role, 'inventory.requestIngredient');
}
// Fase 26A: aprobar/rechazar/mergear propuestas de catálogo — exclusivo de OWNER/MANAGER.
export function canApproveCatalog(role: UserRole | undefined): boolean {
  return hasPermission(role, 'inventory.approveCatalog');
}

export function canManagePurchases(role: UserRole | undefined): boolean {
  return hasPermission(role, 'purchases.manage');
}

/** Confirmar una compra = aprobar el gasto. Un EMPLOYEE captura, pero nunca aprueba la suya. */
export function canConfirmPurchases(role: UserRole | undefined): boolean {
  return hasPermission(role, 'purchases.confirm');
}

/** Fase 21A: separado de canManagePurchases — un EMPLOYEE nunca anula compras directamente. */
export function canVoidPurchase(role: UserRole | undefined): boolean {
  return hasPermission(role, 'purchases.void');
}

export function canAdjustStock(role: UserRole | undefined): boolean {
  return hasPermission(role, 'stock.adjust');
}

export function canTransferStock(role: UserRole | undefined): boolean {
  return hasPermission(role, 'stock.transfer');
}

// --- F1 · Mermas (inventory-waste-employee). Mismos nombres que la matriz del backend. ---

/** Registrar merma: los tres roles, desde la pantalla «Mermas». No abre Inventario ni ajustes. */
export function canRecordWaste(role: UserRole | undefined): boolean {
  return hasPermission(role, 'inventory.recordWaste');
}

/** Revisar la bandeja «Mermas de hoy» y anular: exclusivo de OWNER/MANAGER. */
export function canReviewWaste(role: UserRole | undefined): boolean {
  return hasPermission(role, 'inventory.reviewWaste');
}

// --- F4 · Conteos asignados a ciegas (inventory-count-sheets). Mismos nombres que el backend. ---

/** Contar la lista que te asignaron: los tres roles. No abre existencias ni costos. */
export function canCountAssigned(role: UserRole | undefined): boolean {
  return hasPermission(role, 'inventory.countAssigned');
}

/** Armar, asignar y aprobar listas de conteo: OWNER/MANAGER (el mismo permiso que ajustar existencias). */
export function canManageCountSheets(role: UserRole | undefined): boolean {
  return hasPermission(role, 'stock.adjust');
}

/** Cambiar cuándo una merma se considera grande: solo el dueño (el backend lo aplica igual). */
export function canConfigureWasteThreshold(role: UserRole | undefined): boolean {
  return role === 'OWNER';
}

export function canPostSalesInventory(role: UserRole | undefined): boolean {
  return hasPermission(role, 'salesInventory.post');
}

export function canReverseSalesPosting(role: UserRole | undefined): boolean {
  return hasPermission(role, 'salesInventory.reverse');
}

export function canReprocessSalesPosting(role: UserRole | undefined): boolean {
  // Reservado: el reprocesamiento no existe como funcionalidad todavía (ver PERMISSIONS_MODEL.md).
  return hasPermission(role, 'salesInventory.reprocess');
}

export function canCreateShiftClose(role: UserRole | undefined): boolean {
  return hasPermission(role, 'shiftClose.create');
}

export function canViewShiftCloseHistory(role: UserRole | undefined): boolean {
  return hasPermission(role, 'shiftClose.view');
}

export function canVoidShiftClose(role: UserRole | undefined): boolean {
  return hasPermission(role, 'shiftClose.void');
}

// --- Otros Gastos (Fase 18A) ---

export function canViewExpenses(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherExpenses.view');
}

export function canCreateExpense(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherExpenses.create');
}

export function canConfirmExpense(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherExpenses.confirm');
}

export function canCancelExpense(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherExpenses.void');
}

// --- Ventas Otros (Fase 18D) ---

export function canViewOtherSales(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherSales.view');
}

export function canCreateOtherSale(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherSales.create');
}

export function canConfirmOtherSale(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherSales.confirm');
}

export function canCancelOtherSale(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherSales.void');
}

/**
 * Fase 21C-4A: administrar el catálogo de Productos Otros (crear/editar/activar-desactivar) —
 * espejo del backend, que exige `otherSales.update` (solo OWNER/MANAGER; un EMPLOYEE nunca).
 */
export function canManageOtherSaleProducts(role: UserRole | undefined): boolean {
  return hasPermission(role, 'otherSales.update');
}

/**
 * ONB-001 (Macrofase A): la guía de activación (tarjeta del Inicio y aviso de primera venta en el punto
 * de venta) es del dueño. Espejo del backend, que responde 403 a cualquier otro rol en
 * `/api/onboarding/activation`.
 */
export function canSeeActivationGuide(role: UserRole | undefined): boolean {
  return role === 'OWNER';
}

/**
 * ONB-007 (Macrofase B): «Plan y facturación». Dueño y gerente lo ven (el gerente, solo para consultar);
 * contratar, cancelar, revertir y abrir el portal son solo del dueño. Espejo de `/api/billing`, que
 * responde 403 al gerente en toda acción y al empleado incluso al consultar el detalle.
 */
export function canViewBilling(role: UserRole | undefined): boolean {
  return role === 'OWNER' || role === 'MANAGER';
}

export function canManageBilling(role: UserRole | undefined): boolean {
  return role === 'OWNER';
}

/**
 * PAYPAL-DECLINE-01 · lote 5: cerrar una venta DECLARANDO que no se cobró es solo del dueño, y el
 * servidor lo impone con un 403. El cliente usa esta misma regla para no ofrecer un botón que va a
 * ser rechazado: un gerente llegaba a escribir su declaración y recibía el error al confirmar.
 */
export function canDeclareNativeSaleUnpaid(role: UserRole | undefined): boolean {
  return role === 'OWNER';
}

// --- Solicitudes de anulación (Fase 21A) ---

/** Solo EMPLOYEE crea solicitudes — OWNER/MANAGER anulan directo con canCancelExpense/canCancelOtherSale/etc. */
export function canCreateVoidRequest(role: UserRole | undefined): boolean {
  return hasPermission(role, 'voidRequests.create');
}

/** Ver solicitudes: EMPLOYEE ve las suyas, MANAGER/OWNER ven las de su alcance — el backend decide el filtro exacto. */
export function canViewVoidRequests(role: UserRole | undefined): boolean {
  return hasPermission(role, 'voidRequests.view');
}

/** Aprobar/rechazar: exclusivo de MANAGER/OWNER. */
export function canReviewVoidRequests(role: UserRole | undefined): boolean {
  return hasPermission(role, 'voidRequests.review');
}

/** Cualquier rol autenticado puede "ver" una tienda en el sentido de que `stores` ya viene filtrado por el backend. */
export function canViewStore(): boolean {
  return true;
}

/** Alias semántico de `canViewStore` para flujos operativos (crear compra/ajuste/traspaso/corte en una tienda). */
export function canUseStore(): boolean {
  return true;
}

// --- Regla actor-vs-objetivo de usuarios (espejo de canManageTargetUser en el backend) ---

/**
 * Un OWNER puede administrar a cualquiera. Un MANAGER solo puede administrar a un EMPLOYEE — no a
 * otro MANAGER ni al OWNER (Fase 16B-1, brecha 1: `MANAGER_PEER_PROTECTED`). Un EMPLOYEE no
 * administra a nadie (aunque nunca ve esta pantalla).
 */
export function canManageTargetUser(actorRole: UserRole | undefined, targetRole: UserRole): boolean {
  if (actorRole === 'OWNER') return true;
  if (actorRole === 'MANAGER') return targetRole === 'EMPLOYEE';
  return false;
}

// --- Store scope para reportes financieros (dashboard, costo/margen de ventas, cobertura de recetas) ---

export interface FinancialStoreOptions {
  /** Tiendas que el actor puede elegir en el filtro. Vacío si no tiene ninguna con permiso financiero. */
  selectableStores: AuthStore[];
  /** Si es `true`, el rol puede pedir el agregado de "todas las tiendas" sin elegir una (solo OWNER). */
  allowsAllStores: boolean;
  /** Si es `true`, el actor no tiene ninguna tienda con `canViewFinancials` y no puede ver esta sección en absoluto. */
  hasNoFinancialAccess: boolean;
}

/**
 * Replica `hasFinancialAccess` del backend (Fase 16B-1): un OWNER puede ver el agregado o cualquier
 * tienda; un MANAGER debe elegir una tienda concreta con `canViewFinancials: true`; un EMPLOYEE
 * nunca. Se usa para decidir qué mostrar en el selector de tienda del dashboard y los reportes de
 * costo/margen, y para saber si vale la pena mostrar la sección en absoluto.
 */
export function getFinancialStoreOptions(role: UserRole | undefined, stores: AuthStore[]): FinancialStoreOptions {
  if (role === 'OWNER') {
    return { selectableStores: stores, allowsAllStores: true, hasNoFinancialAccess: false };
  }

  if (role === 'MANAGER') {
    const eligible = stores.filter((store) => store.canViewFinancials);
    return { selectableStores: eligible, allowsAllStores: false, hasNoFinancialAccess: eligible.length === 0 };
  }

  return { selectableStores: [], allowsAllStores: false, hasNoFinancialAccess: true };
}

export function displayName(user: Pick<AuthUser, 'fullName' | 'firstName' | 'lastName' | 'username'> | null | undefined): string {
  // Delega en la fuente unica (utils/userDisplayName), espejo del backend.
  return userDisplayNameOr(user);
}

// --- Store scope para listados/reportes operativos (stock, traspasos, compras, ventas, cortes de turno) ---

export interface StoreScopeOptions {
  /** Si es `true`, el rol puede pedir el agregado de "todas las tiendas" sin elegir una (solo OWNER). */
  allowsAllStores: boolean;
  /** Tienda a preseleccionar por default para un no-OWNER (su primera tienda asignada), o `undefined` si no tiene ninguna o el actor es OWNER. */
  defaultStoreId: string | undefined;
}

/**
 * Fase 16B-3, insumo de docs/PERMISSIONS_GAPS.md: el backend ya no permite a un MANAGER/EMPLOYEE
 * omitir `storeId` en listados/reportes operativos (stock, traspasos, compras, ventas/receipts,
 * cortes de turno) — debe elegir una tienda a la que esté asignado. Este helper evita que la
 * pantalla cargue por default con "todas las tiendas" (lo que ahora siempre falla para no-OWNER) y
 * oculta esa opción del selector, igual que ya hacía `getFinancialStoreOptions` para reportes
 * financieros.
 */
// Fase 23A: formularios/checklists operativos. forms.view = ver la seccion; forms.manage = administrar.
// OWNER y MANAGER; EMPLOYEE no (responder sera un permiso propio de 23B). La seguridad real la aplica
// el backend; esto solo controla navegacion/controles.
export function canViewForms(role: UserRole | undefined): boolean {
  return hasPermission(role, 'forms.view');
}
export function canManageForms(role: UserRole | undefined): boolean {
  return hasPermission(role, 'forms.manage');
}
// Fase 23B: ejecucion (responder) y ver respuestas de otros dentro del alcance.
export function canSubmitForms(role: UserRole | undefined): boolean {
  return hasPermission(role, 'forms.submit');
}
export function canViewFormResponses(role: UserRole | undefined): boolean {
  return hasPermission(role, 'forms.responses.view');
}
// Fase 23C: marcar un hallazgo como "revisado" (auditable). OWNER y MANAGER.
export function canReviewFormResponses(role: UserRole | undefined): boolean {
  return hasPermission(role, 'forms.responses.review');
}

export function getStoreScopeOptions(role: UserRole | undefined, stores: Array<Pick<AuthStore, 'id'>>): StoreScopeOptions {
  if (role === 'OWNER') {
    return { allowsAllStores: true, defaultStoreId: undefined };
  }
  return { allowsAllStores: false, defaultStoreId: stores[0]?.id };
}
